import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  ArrowRight,
  BadgePoundSterling,
  BookOpenCheck,
  Building2,
  Camera,
  Check,
  ClipboardList,
  Download,
  FileText,
  Home,
  Lock,
  LogOut,
  Menu,
  MessageSquareText,
  Play,
  Plus,
  ShieldCheck,
  Sparkles,
  TimerReset,
  UserRound,
  X
} from "lucide-react";
import { client } from "./lib/api";
import { getDeviceId } from "./lib/device";
import type { BillingPlan, CaseBundle, EvidenceCase, EvidenceEntry, LandlordType, User } from "../shared/types";
import "./styles/global.css";

type View = "builder" | "pricing" | "account" | "admin";

const blankEvidence = {
  roomName: "Bedroom",
  kind: "photo_note",
  occurredOn: new Date().toISOString().slice(0, 10),
  title: "Mould visible near window",
  notes: "Black mould has returned after cleaning. The wall feels damp and the room smells musty.",
  severity: 3
};

type CaseFormState = {
  title: string;
  landlordType: LandlordType;
  landlordName: string;
  postcode: string;
  householdNotes: string;
};

type EvidenceFormState = typeof blankEvidence;

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [cases, setCases] = useState<EvidenceCase[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>("");
  const [bundle, setBundle] = useState<CaseBundle | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [billing, setBilling] = useState<{ planCode: string; billingStatus: string; purchases: Array<Record<string, unknown>> } | null>(null);
  const [view, setView] = useState<View>("builder");
  const [authMode, setAuthMode] = useState<"login" | "register">("register");
  const [menuOpen, setMenuOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [caseForm, setCaseForm] = useState({
    title: "Bedroom mould keeps coming back",
    landlordType: "social" as LandlordType,
    landlordName: "Repairs team",
    postcode: "",
    householdNotes: ""
  });
  const [evidenceForm, setEvidenceForm] = useState(blankEvidence);
  const [letterLoading, setLetterLoading] = useState(false);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (location.pathname === "/billing/success" && sessionId) {
      client
        .confirmCheckout(sessionId)
        .then(() => {
          setNotice("Payment confirmed. Your export and case tools are unlocked.");
          history.replaceState({}, "", "/");
        })
        .catch((err) => setError(err.message));
    }
  }, []);

  useEffect(() => {
    Promise.all([client.me(), client.billingConfig()])
      .then(([me, billing]) => {
        setUser(me.user);
        setPlans(billing.plans);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshCases();
    refreshBilling();
  }, [user]);

  useEffect(() => {
    if (!selectedCaseId || !user) return;
    refreshBundle(selectedCaseId);
  }, [selectedCaseId, user]);

  const canExport = useMemo(() => {
    if (!user) return false;
    if (user.planCode === "tracker" && ["active", "trialing", "admin_grant"].includes(user.billingStatus)) return true;
    if (billing?.planCode === "tracker" && ["active", "trialing", "admin_grant"].includes(billing.billingStatus)) return true;
    return Boolean(
      billing?.purchases.some((purchase) => {
        const plan = String(purchase.plan_code || purchase.planCode || "");
        const status = String(purchase.status || "");
        return ["export", "escalation"].includes(plan) && ["paid", "complete"].includes(status);
      })
    );
  }, [billing, user]);

  function refreshCases() {
    client
      .cases()
      .then((payload) => {
        setCases(payload.cases);
        const first = payload.cases[0]?.id || "";
        if (!selectedCaseId && first) setSelectedCaseId(first);
      })
      .catch((err) => setError(err.message));
  }

  function refreshBundle(caseId: string) {
    client
      .getCase(caseId)
      .then(setBundle)
      .catch((err) => setError(err.message));
  }

  function refreshBilling() {
    client
      .billingMe()
      .then((payload) => setBilling(payload.billing as typeof billing))
      .catch(() => undefined);
  }

  async function submitAuth(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const payload =
        authMode === "register"
          ? await client.register({
              email: String(form.get("email")),
              password: String(form.get("password")),
              name: String(form.get("name")),
              deviceId: getDeviceId()
            })
          : await client.login({
              email: String(form.get("email")),
              password: String(form.get("password"))
            });
      setUser(payload.user);
      setNotice("You are in. Start with one room and one dated note.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Auth failed");
    }
  }

  async function submitCase(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    try {
      const created = await client.createCase(caseForm);
      setCases((items) => [created.case, ...items]);
      setSelectedCaseId(created.case.id);
      setNotice("Case created. Add the first dated evidence item.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create case");
    }
  }

  async function submitEvidence(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedCaseId) return;
    setError("");
    try {
      await client.addEvidence(selectedCaseId, evidenceForm);
      await refreshBundle(selectedCaseId);
      setEvidenceForm({ ...blankEvidence, occurredOn: new Date().toISOString().slice(0, 10) });
      setNotice("Evidence logged. Keep adding dated changes and landlord responses.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save evidence");
    }
  }

  async function generateLetter() {
    if (!selectedCaseId) return;
    setLetterLoading(true);
    setError("");
    try {
      await client.generateLetter(selectedCaseId);
      await refreshBundle(selectedCaseId);
      setNotice("Repair letter generated. Review it, then send through email or your repairs portal.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not generate letter");
    } finally {
      setLetterLoading(false);
    }
  }

  async function checkout(planCode: string) {
    if (!user) {
      setView("account");
      setNotice("Create an account first so your pack can be linked to your case.");
      return;
    }
    setError("");
    try {
      const session = await client.checkout(planCode);
      location.href = session.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout failed");
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="/" aria-label="MouldLedger home">
          <span className="brand-mark">
            <ShieldCheck size={21} />
          </span>
          <span>
            <strong>MouldLedger</strong>
            <small>by Lexa</small>
          </span>
        </a>
        <button className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Open navigation">
          <Menu size={22} />
        </button>
        <nav className="desktop-nav">
          <NavButton active={view === "builder"} label="Builder" icon={<ClipboardList size={18} />} onClick={() => setView("builder")} />
          <NavButton active={view === "pricing"} label="Pricing" icon={<BadgePoundSterling size={18} />} onClick={() => setView("pricing")} />
          <NavButton active={view === "account"} label={user ? "Account" : "Sign in"} icon={<UserRound size={18} />} onClick={() => setView("account")} />
          {user?.role === "admin" && (
            <NavButton active={view === "admin"} label="Admin" icon={<Lock size={18} />} onClick={() => setView("admin")} />
          )}
        </nav>
      </header>

      {menuOpen && (
        <div className="drawer">
          <button className="icon-button" onClick={() => setMenuOpen(false)} aria-label="Close navigation">
            <X size={22} />
          </button>
          <NavButton active={view === "builder"} label="Builder" icon={<ClipboardList size={18} />} onClick={() => setView("builder")} />
          <NavButton active={view === "pricing"} label="Pricing" icon={<BadgePoundSterling size={18} />} onClick={() => setView("pricing")} />
          <NavButton active={view === "account"} label={user ? "Account" : "Sign in"} icon={<UserRound size={18} />} onClick={() => setView("account")} />
          {user?.role === "admin" && <NavButton active={view === "admin"} label="Admin" icon={<Lock size={18} />} onClick={() => setView("admin")} />}
        </div>
      )}

      {notice && (
        <button className="notice" onClick={() => setNotice("")}>
          <Check size={16} /> {notice}
        </button>
      )}
      {error && (
        <button className="notice error" onClick={() => setError("")}>
          <AlertTriangle size={16} /> {error}
        </button>
      )}

      {loading ? (
        <section className="loading">Loading MouldLedger</section>
      ) : (
        <>
          {view === "builder" && (
          <BuilderView
              user={user}
              cases={cases}
              bundle={bundle}
              selectedCaseId={selectedCaseId}
              caseForm={caseForm}
              evidenceForm={evidenceForm}
              letterLoading={letterLoading}
              canExport={canExport}
              hasLetter={Boolean(bundle?.letters.length)}
              setCaseForm={setCaseForm}
              setEvidenceForm={setEvidenceForm}
              setSelectedCaseId={setSelectedCaseId}
              submitCase={submitCase}
              submitEvidence={submitEvidence}
              generateLetter={generateLetter}
              onAccount={() => setView("account")}
              onPricing={() => setView("pricing")}
            />
          )}
          {view === "pricing" && <PricingView plans={plans} checkout={checkout} />}
          {view === "account" && (
            <AccountView
              user={user}
              authMode={authMode}
              setAuthMode={setAuthMode}
              submitAuth={submitAuth}
              logout={async () => {
                await client.logout();
                setUser(null);
                setCases([]);
                setBundle(null);
              }}
            />
          )}
          {view === "admin" && user?.role === "admin" && <AdminView />}
        </>
      )}
    </main>
  );
}

function BuilderView(props: {
  user: User | null;
  cases: EvidenceCase[];
  bundle: CaseBundle | null;
  selectedCaseId: string;
  caseForm: CaseFormState;
  evidenceForm: EvidenceFormState;
  letterLoading: boolean;
  canExport: boolean;
  hasLetter: boolean;
  setCaseForm: React.Dispatch<React.SetStateAction<CaseFormState>>;
  setEvidenceForm: React.Dispatch<React.SetStateAction<EvidenceFormState>>;
  setSelectedCaseId: (id: string) => void;
  submitCase: (event: React.FormEvent) => void;
  submitEvidence: (event: React.FormEvent) => void;
  generateLetter: () => void;
  onAccount: () => void;
  onPricing: () => void;
}) {
  return (
    <>
      <section className="hero">
        <div className="hero-media" aria-hidden="true">
          <img src="/hero-mouldledger.png" alt="" />
        </div>
        <div className="hero-content">
          <p className="eyebrow">
            <TimerReset size={16} /> England renter evidence assistant
          </p>
          <h1>Stop arguing about condensation. Build a dated paper trail.</h1>
          <p>
            Log damp and mould evidence, generate a repair report, track the dates that matter, and export a clean bundle for your
            landlord, council, or housing route.
          </p>
          <div className="hero-actions">
            <button className="primary" onClick={props.user ? undefined : props.onAccount}>
              {props.user ? "Start logging below" : "Create free log"} <ArrowRight size={18} />
            </button>
            <button className="secondary" onClick={props.onPricing}>
              <Play size={18} /> See packs
            </button>
          </div>
        </div>
      </section>

      <section className="trust-band">
        <TrustItem icon={<Building2 />} title="Social housing first" text="Awaab's Law guidance is surfaced carefully for England social housing." />
        <TrustItem icon={<Home />} title="Private renters too" text="Build the evidence trail now while private-sector timescales are implemented." />
        <TrustItem icon={<ShieldCheck />} title="No claim promises" text="Documentation support only. No compensation guarantees or legal advice." />
      </section>

      <section className="workspace">
        <div className="panel left-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Case setup</p>
              <h2>One room. One date. One clear record.</h2>
            </div>
            <Plus size={22} />
          </div>
          {!props.user ? (
            <div className="locked-panel">
              <Lock size={28} />
              <h3>Create an account to save evidence</h3>
              <p>Device-bound signup blocks repeat free accounts from the same browser.</p>
              <button className="primary" onClick={props.onAccount}>
                Create account <ArrowRight size={18} />
              </button>
            </div>
          ) : (
            <>
              <form className="stack-form" onSubmit={props.submitCase}>
                <label>
                  Case title
                  <input value={props.caseForm.title} onChange={(event) => props.setCaseForm({ ...props.caseForm, title: event.target.value })} />
                </label>
                <div className="field-grid">
                  <label>
                    Landlord type
                    <select
                      value={props.caseForm.landlordType}
                      onChange={(event) =>
                        props.setCaseForm({ ...props.caseForm, landlordType: event.target.value as LandlordType })
                      }
                    >
                      <option value="social">Social housing</option>
                      <option value="private">Private renting</option>
                      <option value="unknown">Not sure</option>
                    </select>
                  </label>
                  <label>
                    Postcode
                    <input
                      value={props.caseForm.postcode}
                      onChange={(event) => props.setCaseForm({ ...props.caseForm, postcode: event.target.value })}
                      placeholder="SW1A"
                    />
                  </label>
                </div>
                <label>
                  Landlord or repairs team
                  <input
                    value={props.caseForm.landlordName}
                    onChange={(event) => props.setCaseForm({ ...props.caseForm, landlordName: event.target.value })}
                  />
                </label>
                <label>
                  Household note shown in letters
                  <textarea
                    value={props.caseForm.householdNotes}
                    onChange={(event) => props.setCaseForm({ ...props.caseForm, householdNotes: event.target.value })}
                    placeholder="e.g. Tenant, family with young child, asthma concerns recorded separately"
                  />
                </label>
                <button className="primary" type="submit">
                  Create case <Plus size={18} />
                </button>
              </form>

              {props.cases.length > 0 && (
                <div className="case-list">
                  {props.cases.map((item) => (
                    <button
                      className={item.id === props.selectedCaseId ? "case-chip active" : "case-chip"}
                      key={item.id}
                      onClick={() => props.setSelectedCaseId(item.id)}
                    >
                      <span>{item.title}</span>
                      <small>{item.landlordType}</small>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="panel center-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Evidence log</p>
              <h2>{props.bundle?.case.title || "Select or create a case"}</h2>
            </div>
            <Camera size={22} />
          </div>
          {props.user && props.selectedCaseId ? (
            <>
              <form className="stack-form evidence-form" onSubmit={props.submitEvidence}>
                <div className="field-grid">
                  <label>
                    Room
                    <input
                      value={props.evidenceForm.roomName}
                      onChange={(event) => props.setEvidenceForm({ ...props.evidenceForm, roomName: event.target.value })}
                    />
                  </label>
                  <label>
                    Date
                    <input
                      type="date"
                      value={props.evidenceForm.occurredOn}
                      onChange={(event) => props.setEvidenceForm({ ...props.evidenceForm, occurredOn: event.target.value })}
                    />
                  </label>
                </div>
                <label>
                  Evidence title
                  <input
                    value={props.evidenceForm.title}
                    onChange={(event) => props.setEvidenceForm({ ...props.evidenceForm, title: event.target.value })}
                  />
                </label>
                <label>
                  What changed?
                  <textarea
                    value={props.evidenceForm.notes}
                    onChange={(event) => props.setEvidenceForm({ ...props.evidenceForm, notes: event.target.value })}
                  />
                </label>
                <label>
                  Severity: {props.evidenceForm.severity}/5
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={props.evidenceForm.severity}
                    onChange={(event) => props.setEvidenceForm({ ...props.evidenceForm, severity: Number(event.target.value) })}
                  />
                </label>
                <button className="primary" type="submit">
                  Log evidence <Check size={18} />
                </button>
              </form>

              <div className="timeline">
                {(props.bundle?.evidence || []).map((entry) => (
                  <article className="timeline-item" key={entry.id}>
                    <div className="timeline-date">{formatDate(entry.occurredOn)}</div>
                    <div>
                      <h3>{entry.title}</h3>
                      <p>{entry.notes}</p>
                      <small>
                        {entry.roomName} · severity {entry.severity}/5
                      </small>
                    </div>
                  </article>
                ))}
                {!props.bundle?.evidence.length && <p className="muted">Your first evidence item will appear here.</p>}
              </div>
            </>
          ) : (
            <p className="muted">Create an account and a case to start the timeline.</p>
          )}
        </div>

        <div className="panel right-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">AI repair report</p>
              <h2>Letter and export</h2>
            </div>
            <MessageSquareText size={22} />
          </div>
          <div className="deadline-card">
            <h3>Dates to record</h3>
            <ul>
              <li>Emergency hazards: 24 hours for social housing guidance.</li>
              <li>Significant damp/mould: 10 working day investigation.</li>
              <li>Written summary: 3 working days after investigation.</li>
              <li>Safety work: 5 working days after written summary where applicable.</li>
            </ul>
          </div>
          <button className="primary full" disabled={!props.bundle?.evidence.length || props.letterLoading} onClick={props.generateLetter}>
            <Sparkles size={18} /> {props.letterLoading ? "Generating" : "Generate repair letter"}
          </button>
          <div className="letter-box">
            <pre>{props.bundle?.letters[0]?.body || "Generated repair letters appear here. Review before sending."}</pre>
          </div>
          <div className="export-row">
            <a
              className={props.canExport ? "primary link-button" : "secondary link-button"}
              href={props.bundle ? `/api/cases/${props.bundle.case.id}/export` : "#"}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                if (!props.canExport || !props.hasLetter) {
                  event.preventDefault();
                  props.onPricing();
                }
              }}
            >
              <Download size={18} /> Export pack
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

function PricingView({ plans, checkout }: { plans: BillingPlan[]; checkout: (planCode: string) => void }) {
  return (
    <section className="pricing-section">
      <div className="section-intro">
        <p className="eyebrow">
          <BadgePoundSterling size={16} /> Simple UK pricing
        </p>
        <h1>Free to start. Pay when you need a clean evidence bundle.</h1>
        <p>No success fees, no compensation promises, no claims-management pitch.</p>
      </div>
      <div className="pricing-grid">
        {plans.map((plan) => (
          <article className={plan.highlighted ? "price-card featured" : "price-card"} key={plan.code}>
            <div>
              <h2>{plan.name}</h2>
              <strong>{plan.price}</strong>
              <p>{plan.description}</p>
            </div>
            <ul>
              {plan.features.map((feature) => (
                <li key={feature}>
                  <Check size={16} /> {feature}
                </li>
              ))}
            </ul>
            {plan.code === "free" ? (
              <a className="secondary link-button" href="/">
                Start free
              </a>
            ) : (
              <button className="primary" onClick={() => checkout(plan.code)}>
                Buy {plan.name} <ArrowRight size={18} />
              </button>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function AccountView(props: {
  user: User | null;
  authMode: "login" | "register";
  setAuthMode: (mode: "login" | "register") => void;
  submitAuth: (event: React.FormEvent<HTMLFormElement>) => void;
  logout: () => void;
}) {
  if (props.user) {
    return (
      <section className="account-section">
        <div className="panel account-card">
          <p className="eyebrow">
            <UserRound size={16} /> Account
          </p>
          <h1>{props.user.name}</h1>
          <p>{props.user.email}</p>
          <dl className="account-stats">
            <div>
              <dt>Plan</dt>
              <dd>{props.user.planCode}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{humanStatus(props.user.billingStatus)}</dd>
            </div>
          </dl>
          <button className="secondary" onClick={props.logout}>
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="account-section">
      <form className="panel auth-card" onSubmit={props.submitAuth}>
        <p className="eyebrow">
          <Lock size={16} /> {props.authMode === "register" ? "Create account" : "Sign in"}
        </p>
        <h1>{props.authMode === "register" ? "Save the evidence before it gets cleaned over." : "Return to your case."}</h1>
        {props.authMode === "register" && (
          <label>
            Name
            <input name="name" required placeholder="Your name" />
          </label>
        )}
        <label>
          Email
          <input name="email" type="email" required placeholder="you@example.com" />
        </label>
        <label>
          Password
          <input name="password" type="password" required minLength={8} placeholder="Minimum 8 characters" />
        </label>
        <button className="primary" type="submit">
          {props.authMode === "register" ? "Create free account" : "Sign in"} <ArrowRight size={18} />
        </button>
        <button
          className="text-button"
          type="button"
          onClick={() => props.setAuthMode(props.authMode === "register" ? "login" : "register")}
        >
          {props.authMode === "register" ? "Already have an account?" : "Need a free account?"}
        </button>
      </form>
    </section>
  );
}

function AdminView() {
  const [users, setUsers] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    client.adminUsers().then((payload) => setUsers(payload.users));
  }, []);

  async function grant(userId: string, planCode: string) {
    await client.adminGrantPlan(userId, planCode);
    const payload = await client.adminUsers();
    setUsers(payload.users);
  }

  async function setRole(userId: string, role: string) {
    await client.adminSetRole(userId, role);
    const payload = await client.adminUsers();
    setUsers(payload.users);
  }

  return (
    <section className="admin-section">
      <div className="section-intro">
        <p className="eyebrow">
          <Lock size={16} /> Admin
        </p>
        <h1>Users and access</h1>
      </div>
      <div className="admin-table">
        {users.map((row) => (
          <article className="admin-row" key={String(row.id)}>
            <div>
              <strong>{String(row.email)}</strong>
              <small>
                {String(row.name)} · {String(row.plan_code)} · {humanStatus(String(row.billing_status))}
              </small>
            </div>
            <div className="admin-actions">
              <button className="secondary" onClick={() => grant(String(row.id), "tracker")}>
                Grant tracker
              </button>
              <button className="secondary" onClick={() => grant(String(row.id), "free")}>
                Revoke
              </button>
              <button className="secondary" onClick={() => setRole(String(row.id), row.role === "admin" ? "user" : "admin")}>
                {row.role === "admin" ? "Demote" : "Admin"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function NavButton({ active, label, icon, onClick }: { active: boolean; label: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button className={active ? "nav-button active" : "nav-button"} onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}

function TrustItem({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <article>
      <span>{icon}</span>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" }).format(new Date(value));
}

function humanStatus(value: string) {
  const labels: Record<string, string> = {
    free: "Free",
    active: "Active",
    trialing: "Trial",
    admin_grant: "Granted",
    past_due: "Payment needed",
    complete: "Complete"
  };
  return labels[value] || value.replace(/_/g, " ");
}

createRoot(document.getElementById("root")!).render(<App />);
